import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import * as TeamsState from '../../store/TeamsState';
import * as Login from '../../store/Login';
import { TokenHelper } from '../../TokenHelper';
import { useTranslation } from 'react-i18next';
import { Consultant } from '../../interfaces/Consultant';
import { ConsultantActionCreators } from '../../store/consultants';
import { Avatar, Button, InputOnChangeData, Link, PresenceBadge, SearchBox, SearchBoxChangeEvent, Skeleton, SkeletonItem, Table, TableBody, TableCell, TableCellActions, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { AddIcon, CalendarPersonIcon, EditIcon, FolderRefreshIcon, useEditorStyles, useTabStyles } from '../Styles/Editor';
import { useConsultants } from '../Tools/ObjectLoader';

export const ConsultantsTab = () => {
    const { consultants } = useConsultants();
    const consultantsPhotoLoaded = useSelector((state: ApplicationState) => state.consultantsState.consultantsPhotoLoaded);
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState.TeamsState>((state: ApplicationState) => state.teamsState);
    const UserLoginState = useSelector<ApplicationState, Login.UserLoginState>((state: ApplicationState) => state.userNeomyticLogin);
    const { t } = useTranslation();
    const [filter, setFilter] = React.useState<string | null>(null);
    const [filteredConsultants, setFilteredConsultants] = React.useState<Consultant[] | undefined>(undefined);
    const styles = useEditorStyles();
    const stylesTab = useTabStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");

    React.useEffect(() => {
        if (consultants && consultantsPhotoLoaded === false) {
            TokenHelper.GetGraphToken(instance, teamsState, UserLoginState.graphToken)
                .then(token => {
                    if (token) dispatch(ConsultantActionCreators.requestConsultantsPhoto(token));
                });
        }
    }, [instance, teamsState, consultants]);

    React.useEffect(() => {
        if (consultants) {
            let filtered = consultants;
            if (filter && filter.length != 0) {
                filtered = filtered
                    .filter(t => (t.displayName && t.displayName.toLowerCase().includes(filter.toLowerCase())));
            }
            setFilteredConsultants(filtered);
        }
    }, [consultants, filter]);

    const columns = [
        { columnKey: "DisplayName", label: t('DisplayName'), className: stylesTab.width55 },
        { columnKey: "IsAdmin", label: "Is Admin", className: stylesTab.width15andCenter },
        { columnKey: "CanEnterTimeSheet", label: "Can Enter TimeSheet", className: stylesTab.width15andCenter },
        { columnKey: "ReceiveNotification", label: "Receive Notification", className: stylesTab.width15andCenter },
    ];

    return <div className={stylesTab.rootSub}>
        <div className={stylesTab.MainBar}>
            <SearchBox
                placeholder={t('Contains')}
                onChange={(e: SearchBoxChangeEvent, data: InputOnChangeData) => { setFilter(data.value); }}
                dismiss={{ onClick: () => { setFilter(null); } }}
                className={styles.fluid}
            />
            <Button icon={<AddIcon />} shape="circular" title={t('Add')}
                onClick={() => {
                    const c: Consultant = {
                        consultantid: null,
                        displayName: null,
                        email: null,
                        o365id: null,
                        isActive: true,
                        isAdmin: false,
                        receiveNotification: false,
                        receiveInvoiceEmails: false,
                        canEnterTimeSheet: false,
                        canEnterDocuments: false,
                        canManageConsultantDocuments: false,
                        companyName: null,
                        vat: null,
                        iban: null,
                        bic: null,
                        address: null,
                        address2: null,
                        address3: null,
                        postalCode: null,
                        city: null,
                        country: null,
                        firstName: null,
                        lastName: null,
                        phoneNumber: null,
                        companyNumber: null,
                        privateEmail: null,
                        language: null,
                        countryCode: null,
                        website: null,
                        peppolRegisterProd: false,
                        peppolRegisterDev: false,
                        ownPeppolDocsReceptionTeamsNotif: true,
                        ownPeppolDocsReceptionEmailNotif: false,
                        neomyticPeppolDocsReceptionTeamsNotif: false,
                    };
                    dispatch(ConsultantActionCreators.requestConsultantEdition(c));
                }} />
        </div>
        <h2>Consultants</h2>
        <Table arial-label="Default table">
            <TableHeader>
                <TableRow>
                    {columns.map((column) => (
                        <TableHeaderCell key={column.columnKey} className={column.className}>
                            {column.label}
                        </TableHeaderCell>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {filteredConsultants === undefined
                    ? [...Array(10)].map((_, index) =>
                        <TableRow key={index}>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                            <TableCell>
                                <Skeleton animation="pulse">
                                    <SkeletonItem className={stylesTab.width15} />
                                </Skeleton>
                            </TableCell>
                        </TableRow>)
                    : filteredConsultants.map(co =>
                        <TableRow key={co.consultantid}>
                            <TableCell>
                                <TableCellLayout
                                    media={
                                        <Avatar
                                            aria-label={co.displayName ?? ""}
                                            name={co.displayName ?? ""}
                                            image={co.photo
                                                ? { src: co.photo }
                                                : undefined
                                            }
                                        />
                                    }
                                >
                                    <Button appearance="transparent" onClick={() => dispatch(ConsultantActionCreators.requestConslutantQuickView(co))}>{co.displayName}</Button>
                                </TableCellLayout>
                                <TableCellActions>
                                    <Button
                                        icon={<EditIcon />}
                                        appearance="subtle"
                                        aria-label={t('Edit')}
                                        onClick={() => dispatch(ConsultantActionCreators.requestConsultantEdition(co))}
                                    />
                                    <Button
                                        icon={<CalendarPersonIcon />}
                                        appearance="subtle"
                                        aria-label={t('Edit')}
                                        onClick={() => dispatch(ConsultantActionCreators.requestConsultantActivityPeriodesEdition(co))}
                                    />
                                    <Button
                                        icon={<FolderRefreshIcon />}
                                        appearance="subtle"
                                        aria-label={t('Edit')}
                                        onClick={() => {
                                            TokenHelper.GetToken(instance, teamsState)
                                                .then(token => {
                                                    if (token !== null) {
                                                        const toastId = (Math.random() + 1).toString(36).substring(7);
                                                        dispatchToast(
                                                            <Toast appearance="inverted">
                                                                <ToastTitle>
                                                                    Checking Folder structure...
                                                                </ToastTitle>
                                                            </Toast>,
                                                            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                                                        );
                                                        fetch("api/ConsultantsDocuments/CheckDocumentStruct/" + co.consultantid, {
                                                            method: "PATCH",
                                                            headers: {
                                                                authorization: "bearer " + token,
                                                            },
                                                            mode: "cors",
                                                            cache: "default",
                                                        })
                                                            .then(response => {
                                                                if (response.status === 200 || response.status === 204) {
                                                                    updateToast({
                                                                        content: (
                                                                            <Toast appearance="inverted">
                                                                                <ToastTitle action={
                                                                                    <ToastTrigger>
                                                                                        <Link>Dismiss</Link>
                                                                                    </ToastTrigger>
                                                                                }>
                                                                                    Folder structure applyed
                                                                                </ToastTitle>
                                                                            </Toast>
                                                                        ),
                                                                        intent: "success",
                                                                        toastId,
                                                                        timeout: 5000,
                                                                    });
                                                                }
                                                                else {
                                                                    updateToast({
                                                                        content: (
                                                                            <Toast appearance="inverted">
                                                                                <ToastTitle action={
                                                                                    <ToastTrigger>
                                                                                        <Link>Dismiss</Link>
                                                                                    </ToastTrigger>
                                                                                }>
                                                                                    Folder structure failed
                                                                                </ToastTitle>
                                                                            </Toast>
                                                                        ),
                                                                        intent: "error",
                                                                        toastId,
                                                                        timeout: 10000,
                                                                    });
                                                                }
                                                            });
                                                    }
                                                });
                                        }}
                                    />
                                </TableCellActions>
                            </TableCell>
                            <TableCell>{co.isAdmin ? <PresenceBadge status="available" /> : <PresenceBadge status="blocked" />}</TableCell>
                            <TableCell>{co.canEnterTimeSheet ? <PresenceBadge status="available" /> : <PresenceBadge status="blocked" />}</TableCell>
                            <TableCell>{co.receiveNotification ? <PresenceBadge status="available" /> : <PresenceBadge status="blocked" />}</TableCell>
                        </TableRow>
                    )}
            </TableBody>
        </Table>
    </div>;
};